/**
 * Минимальный обвес над Web Crypto Subtle для шифрования analytics-кредов
 * перед сохранением в sessionStorage. AES-GCM ключ выводится PBKDF2 из
 * основного metadoor-токена — значит:
 *   - пока юзер залогинен в основной системе (cookie 'token' жива) — мы
 *     можем расшифровать пароль и продлить analytics-сессию без ввода;
 *   - как только main-токен сменился/истёк, расшифровка падает — это
 *     наша security-граница, дальше нужен полный signIn.
 *   - в DevTools / sessionStorage видна только base64-белиберда.
 * Это не защищает от XSS в принципе (атакующий может взять main-токен и
 * расшифровать сам), но убирает пароль из видимого хранилища.
 */

const TEXT = new TextEncoder();
const DECODER = new TextDecoder();
const SALT = TEXT.encode('analytics-session-v1');
const PBKDF2_ITERS = 100_000;

async function deriveKey(seed: string): Promise<CryptoKey> {
  const baseKey = await crypto.subtle.importKey(
    'raw',
    TEXT.encode(seed),
    'PBKDF2',
    false,
    ['deriveKey'],
  );
  return crypto.subtle.deriveKey(
    { name: 'PBKDF2', salt: SALT, iterations: PBKDF2_ITERS, hash: 'SHA-256' },
    baseKey,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt'],
  );
}

function toB64(bytes: Uint8Array): string {
  let s = '';
  for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i]);
  return btoa(s);
}

function fromB64(b64: string): Uint8Array {
  const s = atob(b64);
  const out = new Uint8Array(s.length);
  for (let i = 0; i < s.length; i++) out[i] = s.charCodeAt(i);
  return out;
}

export async function encryptWithSeed(plaintext: string, seed: string): Promise<string> {
  const key = await deriveKey(seed);
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ct = new Uint8Array(
    await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, TEXT.encode(plaintext)),
  );
  const packed = new Uint8Array(iv.length + ct.length);
  packed.set(iv, 0);
  packed.set(ct, iv.length);
  return toB64(packed);
}

export async function decryptWithSeed(packed: string, seed: string): Promise<string | null> {
  try {
    const bytes = fromB64(packed);
    const iv = bytes.slice(0, 12);
    const ct = bytes.slice(12);
    const key = await deriveKey(seed);
    const pt = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, ct);
    return DECODER.decode(pt);
  } catch {
    // Неверный seed (сменился main-токен) или повреждённый payload — клиент
    // должен вернуться к полному signIn.
    return null;
  }
}
